import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { executionsApi, overridesApi, workflowsApi } from '@/services/workflows';
import type { CreateOverridePayload } from '@/types/workflow';

export const workflowKeys = {
  all: ['workflows'] as const,
  list: (active?: boolean) => [...workflowKeys.all, 'list', { active }] as const,
  detail: (id: string) => [...workflowKeys.all, 'detail', id] as const,
  executions: (id: string) => [...workflowKeys.all, 'executions', id] as const,
  overrides: (id: string) => [...workflowKeys.all, 'overrides', id] as const,
  stats: (id: string) => [...workflowKeys.all, 'stats', id] as const,
};

export const executionKeys = {
  all: ['executions'] as const,
  list: (params: object) => [...executionKeys.all, 'list', params] as const,
  detail: (id: string) => [...executionKeys.all, 'detail', id] as const,
};

export const overrideKeys = {
  all: ['overrides'] as const,
};

export function useWorkflows(active?: boolean) {
  return useQuery({
    queryKey: workflowKeys.list(active),
    queryFn: () => workflowsApi.list(active),
    refetchInterval: 60_000,
  });
}

export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.detail(id ?? ''),
    queryFn: () => workflowsApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useWorkflowExecutions(id: string | undefined, limit = 20) {
  return useQuery({
    queryKey: workflowKeys.executions(id ?? ''),
    queryFn: () => workflowsApi.executions(id!, limit),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });
}

export function useWorkflowStats(id: string | undefined, sample = 50) {
  return useQuery({
    queryKey: workflowKeys.stats(id ?? ''),
    queryFn: () => workflowsApi.stats(id!, sample),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });
}

export function useWorkflowOverrides(id: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.overrides(id ?? ''),
    queryFn: () => workflowsApi.listOverrides(id!),
    enabled: Boolean(id),
  });
}

export function useExecutions(params: { status?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: executionKeys.list(params),
    queryFn: () => executionsApi.list(params),
    refetchInterval: 60_000,
  });
}

export function useExecution(id: string | undefined) {
  return useQuery({
    queryKey: executionKeys.detail(id ?? ''),
    queryFn: () => executionsApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useTriggerWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workflowsApi.trigger(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: workflowKeys.executions(id) });
      qc.invalidateQueries({ queryKey: workflowKeys.stats(id) });
      qc.invalidateQueries({ queryKey: executionKeys.all });
    },
  });
}

export function useRerunExecution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => executionsApi.rerun(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: workflowKeys.executions(data.workflow_id) });
      qc.invalidateQueries({ queryKey: workflowKeys.stats(data.workflow_id) });
      qc.invalidateQueries({ queryKey: executionKeys.all });
    },
  });
}

export function useToggleWorkflowActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? workflowsApi.activate(id) : workflowsApi.deactivate(id),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: workflowKeys.detail(id) });
      qc.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

export function useCreateOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateOverridePayload }) =>
      workflowsApi.createOverride(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workflowKeys.all });
      qc.invalidateQueries({ queryKey: overrideKeys.all });
    },
  });
}

export function useAllOverrides() {
  return useQuery({
    queryKey: overrideKeys.all,
    queryFn: () => overridesApi.listAll(),
  });
}
